import { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import queryString from 'query-string';
import {
  ButtonPrimary,
  ButtonPseudo,
  SidePanel,
  SidePanelType,
  PermissionEnum,
  Popup,
  IconSelected,
  ChangeLogs,
  PermissionDenied,
  notificationCategoriesTree,
} from '@ui/src';
import { useLazyGetEmployeesDictionariesQuery } from '@administration/store/dictionaries/service';
import {
  NotificationCategoryEnum,
  NotificationItem,
} from '@notifications/hoc/center/types';
import NotificationCenterFilters from '@notifications/components/notification-center/filters-sidepanel';
import NotificationCenterMainColumn from '@notifications/components/notification-center/main-column';
import NotificationsControlsPanel from '@notifications/components/notification-center/controls-panel';
import EditNotificationsSidepanel from '@notifications/components/notification-center/edit-sidepanel';
import NotificationCommentColumn from '@notifications/components/notification-center/comment-column';
import NotificationEditColumn from '@notifications/components/notification-center/notification-edit-column';
import { useAppSelector, useAppDispatch } from '@store';
import {
  setSelectedNotifications,
  clearChangeLogs,
  clearNotificationComments,
  setSelectedNotification,
  setSelectedNotificationId,
} from './store/slices';
import {
  useLazyGetReportsNotificationEmployeesQuery,
  useLazyGetReportsNotificationItemQuery,
} from './store/services';
import useRequests from './hooks/use-request';

import cn from './notification-center.module.css';

const NotificationCenter = () => {
  const [dirtyPopupVisible, setDirtyPopupVisible] = useState(false);
  const [editIssueDirtyPopupVisible, setEditIssueDirtyPopupVisible] =
    useState(false);
  const [prepareSelectedNotification, setPrepareSelectedNotification] =
    useState<NotificationItem | undefined>(undefined);

  const [selectedGroup, setSelectedGroup] = useState('');

  const { search } = useLocation();
  const navigate = useNavigate();

  const categories = notificationCategoriesTree?.map((item) => ({
    name: item.page
      ?.split('_')
      .map((page) => `${page.charAt(0)}${page.slice(1).toLowerCase()}`)
      .join(' '),
    value: item.page,
  }));

  const groups = notificationCategoriesTree
    ?.find((item) => item.page === NotificationCategoryEnum.REPORTS)
    ?.groups?.map((item) => ({
      name: item.group
        ?.split('_')
        ?.map((group) => `${group.charAt(0)}${group.slice(1).toLowerCase()}`)
        .join(' '),
      value: item.group,
    }));

  const types = selectedGroup
    ? notificationCategoriesTree
        ?.find((item) => item.page === NotificationCategoryEnum.REPORTS)
        ?.groups?.find((item) => item.group === selectedGroup)
        ?.types?.map((item) => ({
          name: item.action
            ?.replace('_ACTION', '')
            ?.split('_')
            ?.map(
              (action: string) =>
                `${action.charAt(0)}${action.slice(1).toLowerCase()}`,
            )
            .join(' '),
          value: item.action,
        }))
    : undefined;

  const {
    customersDictionaries: { activePermissions },
    administrationDictionaries: { activeUsers, users },
    reportsNotificationCenter: {
      notifications,
      selectedNotifications,
      pagination,
      comments,
      changeLogs,
      changeLogsPagination,
      notificationEntity,
      selectedNotificationId,
      selectedNotification,
    },
    auth: { user },
  } = useAppSelector((state) => state);

  const dispatch = useAppDispatch();

  const [getEmployeeDictionaries] = useLazyGetEmployeesDictionariesQuery();
  const [getReportsNotificationsEmployees] =
    useLazyGetReportsNotificationEmployeesQuery();
  const [getReportsNotificationItem] = useLazyGetReportsNotificationItemQuery();

  const {
    scrollRef,
    control,
    reset,
    setValue,
    editControl,
    editReset,
    isDirty,
    editIssueControl,
    editIssueReset,
    editIssueIsDirty,
    errors,
    filterFields,
    getChangeLogsAction,
    comment,
    setComment,
    commentError,
    onSendComment,
    getNotificationListAction,
    nextPageIsLoading,
    onScroll,
    editError,
    setEditError,
    onEditIssueSubmit,
    selectedCategories,
    setSelectedCategories,
    selectedGroups,
    setSelectedGroups,
    selectedActions,
    setSelectedActions,
    onCategoryFilterApply,
    deadlineFilter,
    selectedStatuses,
    setSelectedStatuses,
    selectedExecutorId,
    setSelectedExecutorId,
    onApplyStatusFilter,
    onApplyExecutorFilter,
    onApplyDeadlineFilter,
    onClearDeadlineFilter,
    onClearExecutorFilter,
    onClearStatusFilter,
    onCategoryFilterClear,
    panelIsVisible,
    setPanelIsVisible,
    onFiltersSubmit,
    selectedStatus,
    setSelectedStatus,
    setPrevSelectedNotification,
    changeLogsPanelIsOpen,
    setChangeLogPanelIsOpen,
    selectedDeadline,
    setSelectedDeadline,
    batchUpdateError,
    setBatchUpdateError,
    editSidepanelVisible,
    setEditSidepanelVisible,
    onEditSubmit,
    statusesChangeNotificationVisible,
    setStatusChangeNotificationVisible,
    deadlineChangeNotificationVisible,
    setDeadlineChangeNotificationVisible,
    onEditSubmitFromControlPanel,
    updateIssueCommentAction,
    setStopRequest,
    batchErrors,
    setEditIssueValue,
    massEditIssueFields,
    setMassEditValue,
    addCommentRequestInProgress,
    isSuccess,
    changeLogsIsLoading,
    changeLogsIsFetching,
    isLoading,
    isFetching,
    updateInProgress,
    commentsIsLoading,
    commentsIsFetching,
    batchUpdateInProgress,
    batchUpdateIsSuccess,
    batchAddIssueCommentInProgress,
    updateIssueCommentInProgress,
    updateIssueCommentIsSuccess,
  } = useRequests({
    defaultFiltersValue: {
      createdAt: [],
      createdAtFrom: '',
      createdAtTo: '',
      deadline: [],
      deadlineFrom: '',
      deadlineTo: '',
      executorId: [],
      group: [],
      responsibleId: [],
      status: [],
      type: [],
    },
    selectedNotification,
    pagination,
    search,
    changeLogs,
    activePermissions,
    commentsListAvailable: activePermissions?.includes(
      PermissionEnum.NOTIFICATIONS_REPORTS_COMMENTS_LIST,
    ),
    withoutCategoryFilter: true,
    selectedNotifications,
    setSelectedNotifications: (ids: string[]) =>
      dispatch(setSelectedNotifications(ids)),
    selectedNotificationId,
  });

  const onChangeNotificationItem = (data: NotificationItem) => {
    setStopRequest(true);

    let nextQuery = search
      ?.replace('?', '')
      ?.split('&')
      .filter((item) => !item.includes('id'))
      .join('&');

    nextQuery = `${nextQuery}${nextQuery?.length > 0 ? '&' : ''}id=${data?.id}`;

    navigate(`?${nextQuery}`);

    setPrevSelectedNotification(selectedNotification);
    dispatch(setSelectedNotification(data));
    dispatch(setSelectedNotificationId(data?.id));
    dispatch(clearNotificationComments());
    editIssueReset({
      status: '',
      responsibleId: '',
      executorId: '',
      deadline: '',
    });
    setEditError('');

    if (prepareSelectedNotification) {
      setPrepareSelectedNotification(undefined);
    }
  };

  useEffect(() => {
    if (batchUpdateIsSuccess) {
      editReset({
        status: '',
        responsibleId: '',
        executorId: '',
        deadline: '',
        comment: '',
        ids: [],
      });
    }
  }, [batchUpdateIsSuccess]);

  useEffect(() => {
    if (
      !activeUsers &&
      activePermissions &&
      activePermissions?.includes(PermissionEnum.USERS_LIST)
    ) {
      getEmployeeDictionaries();
      getReportsNotificationsEmployees();
    }
  }, [activeUsers, activePermissions]);

  useEffect(() => {
    if (
      activePermissions?.includes(PermissionEnum.NOTIFICATIONS_REPORTS_LIST) &&
      !search
    ) {
      getNotificationListAction();
    }
  }, [activePermissions, search]);

  useEffect(() => {
    if (statusesChangeNotificationVisible) {
      setTimeout(() => {
        setStatusChangeNotificationVisible(false);
        setSelectedStatus('');
      }, 3000);
    }
  }, [statusesChangeNotificationVisible]);

  useEffect(() => {
    if (deadlineChangeNotificationVisible) {
      setTimeout(() => {
        setDeadlineChangeNotificationVisible(false);
        setSelectedDeadline('');
      }, 3000);
    }
  }, [deadlineChangeNotificationVisible]);

  useEffect(() => {
    if (notifications && notifications?.length && !selectedNotification) {
      if (!search?.includes('id')) {
        dispatch(setSelectedNotification(notifications[0]));
        dispatch(setSelectedNotificationId(notifications[0].id));
        setStopRequest(true);
        navigate(`?id=${notifications[0].id}`);
      } else if (search.includes('id')) {
        const parsed = queryString.parse(search);

        if (parsed.id) {
          const currentNotification = notifications?.find(
            (item) => item.id === parsed.id,
          );

          if (currentNotification) {
            dispatch(setSelectedNotification(currentNotification));
            dispatch(setSelectedNotificationId(currentNotification.id));
          } else {
            getReportsNotificationItem(parsed.id as string);
          }
        }
      }
    } else if (!notifications?.length && selectedNotification) {
      dispatch(setSelectedNotification(undefined));
      dispatch(setSelectedNotificationId(''));

      if (search.includes('id')) {
        setStopRequest(true);
        const nextSearch = search
          ?.split('&')
          ?.filter((item) => !item.includes('id'))
          ?.join('&');

        navigate(`${nextSearch?.charAt(0) !== '?' ? '?' : ''}${nextSearch}`);
      }
    }
  }, [selectedNotification, notifications, search]);

  useEffect(() => {
    if (isSuccess) {
      setComment('');
    }
  }, [isSuccess]);

  useEffect(() => {
    if (selectedNotification && selectedNotification.issue) {
      editIssueReset({
        status: selectedNotification.issue.status || '',
        responsibleId: selectedNotification.issue.responsibleId || '',
        executorId: selectedNotification.issue.executorId || '',
        deadline: selectedNotification.issue.deadline || '',
      });
    }
  }, [selectedNotification]);

  useEffect(
    () => () => {
      dispatch(clearNotificationComments());
    },
    [],
  );

  let content;

  if (
    activePermissions &&
    activePermissions.includes(PermissionEnum.NOTIFICATIONS_REPORTS_LIST)
  ) {
    content = (
      <div className={cn.notificationCenterWrapper}>
        <NotificationCenterMainColumn
          selectedCategories={selectedCategories}
          setSelectedCategories={setSelectedCategories}
          selectedGroups={selectedGroups}
          setSelectedGroups={setSelectedGroups}
          selectedActions={selectedActions}
          setSelectedActions={setSelectedActions}
          setPanelIsVisible={setPanelIsVisible}
          onCategoryFilterApply={onCategoryFilterApply}
          onCategoryFilterClear={onCategoryFilterClear}
          selectedStatuses={selectedStatuses}
          setSelectedStatuses={setSelectedStatuses}
          selectedExecutorId={selectedExecutorId}
          setSelectedExecutorId={setSelectedExecutorId}
          employees={activeUsers}
          notifications={notifications}
          selectedNotifications={selectedNotifications}
          selectedNotificationId={selectedNotification?.id}
          setSelectedNotification={(data) => {
            if (editIssueIsDirty) {
              setEditIssueDirtyPopupVisible(true);
              setPrepareSelectedNotification(data);
            } else {
              onChangeNotificationItem(data);
            }
          }}
          categories={categories}
          groups={groups}
          types={types}
          selectedCategory={NotificationCategoryEnum.REPORTS}
          selectedGroup={selectedGroup}
          setSelectedGroup={setSelectedGroup}
          setSelectedNotifications={(notificationsList: string[]) =>
            dispatch(setSelectedNotifications(notificationsList))
          }
          onApplyStatusFilter={onApplyStatusFilter}
          onClearStatusFilter={onClearStatusFilter}
          onApplyExecutorFilter={onApplyExecutorFilter}
          onClearExecutorFilter={onClearExecutorFilter}
          deadlineFilter={deadlineFilter}
          setDeadlineFilter={onApplyDeadlineFilter}
          onClearDeadlineFilter={onClearDeadlineFilter}
          isLoading={isLoading}
          isFetching={isFetching}
          filterFields={filterFields}
          scrollRef={scrollRef}
          onScroll={onScroll}
          nextPageIsLoading={nextPageIsLoading}
          categoryFilterTitle="Group"
          withoutCategoryBackButton
        />
        <NotificationCommentColumn
          notification={selectedNotification}
          comment={comment}
          setComment={setComment}
          commentAvailable={activePermissions?.includes(
            PermissionEnum.NOTIFICATIONS_REPORTS_COMMENTS_CREATE_ACTION,
          )}
          editCommentsAvailable={activePermissions?.includes(
            PermissionEnum.NOTIFICATIONS_REPORTS_COMMENTS_EDIT_ACTION,
          )}
          commentsListAvailable={activePermissions?.includes(
            PermissionEnum.NOTIFICATIONS_REPORTS_COMMENTS_LIST,
          )}
          onSendComment={onSendComment}
          requestInProgress={addCommentRequestInProgress}
          commentsIsLoading={commentsIsLoading || commentsIsFetching}
          commentError={commentError}
          comments={comments}
          notificationEntity={notificationEntity}
          updateIssueCommentAction={updateIssueCommentAction}
          updateIssueCommentInProgress={updateIssueCommentInProgress}
          updateIssueCommentIsSuccess={updateIssueCommentIsSuccess}
        />
        <NotificationEditColumn
          notification={selectedNotification}
          employees={activeUsers}
          setChangeLogPanelIsOpen={setChangeLogPanelIsOpen}
          auditLogAvailable={activePermissions?.includes(
            PermissionEnum.AUDIT_LOG_LIST,
          )}
          editIssueControl={editIssueControl}
          isDirty={editIssueIsDirty}
          onEditIssueSubmit={onEditIssueSubmit}
          editError={editError}
          updateInProgress={updateInProgress}
          errors={errors}
          editAvailable={activePermissions.includes(
            PermissionEnum.NOTIFICATIONS_REPORTS_EDIT_ACTION,
          )}
          setEditIssueValue={setEditIssueValue}
          currentUser={user}
        />
        <SidePanel
          type={SidePanelType.Accounts}
          isOpen={panelIsVisible}
          title="Filters"
          onClose={() => {
            setPanelIsVisible(false);

            if (selectedGroups?.length && !filterFields.group?.length) {
              setValue('group', selectedGroups);
            }

            if (selectedActions?.length && !filterFields?.type?.length) {
              setValue('type', selectedActions);
            }
          }}
          sidepanelExpandedKey="notification-center-filters"
          footer={
            <div className={cn.filtersSidepanelFooter}>
              <ButtonPrimary
                text="Apply filters"
                onClick={onFiltersSubmit}
                bold
              />
              <ButtonPseudo
                text="Clear all"
                onClick={() =>
                  reset({
                    group: [],
                    type: [],
                    status: [],
                    responsibleId: [],
                    executorId: [],
                    createdAt: [],
                    createdAtFrom: '',
                    createdAtTo: '',
                    deadline: [],
                    deadlineFrom: '',
                    deadlineTo: '',
                  })
                }
                bold
              />
            </div>
          }
          closeOnClickOutside
        >
          <NotificationCenterFilters
            control={control}
            users={activeUsers}
            setValue={setValue}
            onReset={() => {
              setValue('deadlineFrom', '');
              setValue('deadlineTo', '');
              setValue('deadline', []);
            }}
            filterFields={filterFields}
            category={NotificationCategoryEnum.REPORTS}
            currentUser={user}
            withoutCategories
          />
        </SidePanel>
        <SidePanel
          type={SidePanelType.Accounts}
          isOpen={editSidepanelVisible}
          title="Mass edit"
          onClose={() => {
            if (isDirty) {
              setDirtyPopupVisible(true);
            } else {
              setEditSidepanelVisible(false);
              editReset({});
            }
          }}
          sidepanelExpandedKey="notification-center-mass-edit"
          errorSidepanel={batchUpdateError}
          footer={
            <>
              <ButtonPrimary
                text={
                  batchUpdateInProgress || batchAddIssueCommentInProgress
                    ? 'Loading...'
                    : 'Save tasks'
                }
                onClick={onEditSubmit}
                disabled={
                  batchUpdateInProgress ||
                  batchAddIssueCommentInProgress ||
                  !isDirty
                }
                bold
              />
              <ButtonPseudo
                text="Close"
                onClick={() => {
                  if (isDirty) {
                    setDirtyPopupVisible(true);
                  } else {
                    setEditSidepanelVisible(false);
                    editReset({
                      status: '',
                      responsibleId: '',
                      executorId: '',
                      deadline: '',
                      comment: '',
                      ids: [],
                    });
                    setBatchUpdateError('');
                  }
                }}
                bold
              />
            </>
          }
          closeOnClickOutside
        >
          <EditNotificationsSidepanel
            selectedNotifications={selectedNotifications}
            control={editControl}
            employees={activeUsers}
            batchUpdateInProgress={
              batchUpdateInProgress || batchAddIssueCommentInProgress
            }
            errors={batchErrors}
            currentUser={user}
            editIssueFields={massEditIssueFields}
            setValue={setMassEditValue}
          />
        </SidePanel>
        <SidePanel
          isOpen={changeLogsPanelIsOpen}
          onClose={() => {
            setChangeLogPanelIsOpen(false);
            dispatch(clearChangeLogs());
          }}
          title="Notification task change logs"
          type={SidePanelType.Accounts}
          sidepanelExpandedKey="notification-center-task-change-logs"
          footer={
            <div>
              <ButtonPseudo
                text="Close"
                onClick={() => setChangeLogPanelIsOpen(false)}
                bold
              />
            </div>
          }
          closeOnClickOutside
        >
          <>
            {activePermissions?.includes(PermissionEnum.AUDIT_LOG_LIST) && (
              <ChangeLogs
                entityType={['ISSUE', 'ISSUE_COMMENT']}
                entityId={selectedNotification?.issue?.id as string}
                changeLogsIsLoading={changeLogsIsLoading}
                changeLogs={changeLogs}
                activePermission={activePermissions}
                users={users}
                pagination={changeLogsPagination}
                changeLogsIsFetching={changeLogsIsFetching}
                getChangeLogs={getChangeLogsAction}
              />
            )}
            {!activePermissions?.includes(PermissionEnum.AUDIT_LOG_LIST) && (
              <PermissionDenied />
            )}
          </>
        </SidePanel>
        <Popup
          title="You have unsaved changes"
          description=""
          onCancelClick={() => setEditIssueDirtyPopupVisible(false)}
          onOkClick={() => {
            setEditIssueDirtyPopupVisible(false);

            onChangeNotificationItem(
              prepareSelectedNotification as NotificationItem,
            );
          }}
          cancelButtonText="Cancel"
          okButtonText="Close"
          isVisible={editIssueDirtyPopupVisible}
          parent={document.getElementById('dirty-popup') as HTMLElement}
        />
        <Popup
          title="You have unsaved changes"
          description=""
          onCancelClick={() => setDirtyPopupVisible(false)}
          onOkClick={() => {
            setDirtyPopupVisible(false);
            dispatch(setSelectedNotifications([]));
            setEditSidepanelVisible(false);
            editReset({
              status: '',
              responsibleId: '',
              executorId: '',
              deadline: '',
              comment: '',
              ids: [],
            });
            setBatchUpdateError('');
          }}
          cancelButtonText="Cancel"
          okButtonText="Close"
          isVisible={dirtyPopupVisible}
          parent={document.getElementById('dirty-popup') as HTMLElement}
        />
        {statusesChangeNotificationVisible && (
          <div className={cn.statusChangeNotification}>
            <div className={cn.statusChangeNotificationContent}>
              <IconSelected />
              {`Status changed to ${selectedStatus}`}
            </div>
          </div>
        )}
        {deadlineChangeNotificationVisible && (
          <div className={cn.statusChangeNotification}>
            <div className={cn.statusChangeNotificationContent}>
              <IconSelected />
              {`Deadline changed to ${new Date(
                selectedDeadline,
              ).toLocaleDateString()}`}
            </div>
          </div>
        )}
        {selectedNotifications &&
          selectedNotifications?.length > 0 &&
          !editSidepanelVisible && (
            <NotificationsControlsPanel
              selectedNotifications={selectedNotifications}
              notificationsLength={notifications?.length}
              onSelectedAll={() => {
                if (selectedNotifications?.length !== notifications?.length) {
                  dispatch(
                    setSelectedNotifications(
                      notifications?.map((item) => item.id) as string[],
                    ),
                  );
                } else {
                  dispatch(setSelectedNotifications([]));
                }
              }}
              setEditSidepanelVisible={setEditSidepanelVisible}
              setSelectedStatus={setSelectedStatus}
              setSelectedDeadline={setSelectedDeadline}
              onClose={() => dispatch(setSelectedNotifications([]))}
              onSubmit={onEditSubmitFromControlPanel}
            />
          )}
        <div id="dirty-popup" />
      </div>
    );
  } else if (
    activePermissions &&
    !activePermissions?.includes(PermissionEnum.NOTIFICATIONS_REPORTS_LIST)
  ) {
    content = <PermissionDenied />;
  }

  return content;
};

export default NotificationCenter;
